import { usePageRefresh } from '../../../../context/PageRefreshContext';
import { localDateString } from '../../../../utils/helpers/localDate';
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Icon } from '@iconify/react';
import SearchableDropdown from '../../../../components/SearchableDropdown';
import { getJournals, deleteJournal } from '../../../../redux/actions/SocietyPortal/JournalAction/JournalAction';
import { selectedSocietyStorer } from '../../../../utils/webStorer';
import { SOCIETY_PORTAL, SOCIETY_PORTAL_ACCOUNTS_JOURNAL_ADD, SOCIETY_PORTAL_ACCOUNTS_JOURNAL_EDIT, SOCIETY_PORTAL_ACCOUNTS_JOURNAL_BULK_IMPORT } from '../../../../Routing/routes';
import ViewJournalModal from './ViewJournalModal';
import DeleteJournalModal from './DeleteJournalModal';
import AuditLogsModal from './AuditLogsModal';

const ENTRY_TYPES = ['All Types', 'payment', 'receipt', 'daily', 'opening', 'closing', 'debitnote', 'creditnote', 'advance', 'invoice', 'debit_receipt', 'facility', 'facility_receipt', 'expense', 'other'];
const ENTRY_TYPE_LABELS = {
    payment: 'Payment',
    receipt: 'Receipt',
    daily: 'Daily',
    opening: 'Opening',
    closing: 'Closing',
    debitnote: 'Debit Note',
    creditnote: 'Credit Note',
    advance: 'Advance',
    invoice: 'Invoice',
    debit_receipt: 'Debit Receipt',
    facility: 'Facility',
    facility_receipt: 'Facility Receipt',
    expense: 'Expense',
    other: 'Other',
};

const ENTRY_FROM_OPTIONS = ['All Sources', 'manual', 'import', 'system'];
// eslint-disable-next-line no-unused-vars
const _ENTRY_FROM_LABELS = {
    journal: 'Journal',
    import: 'Excel /Csv Upload'
};

const Journal = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { journalsData, pagination, loading, deleteLoading } = useSelector(state => state.JournalReducer);

    const [filterEntryType, setFilterEntryType] = useState('All Types');
    const [filterEntryFrom, setFilterEntryFrom] = useState('All Sources');
    const [fromDate, setFromDate] = useState('');
    const [toDate, setToDate] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [limit] = useState(10);
    const [expandedId, setExpandedId] = useState(null);
    const [viewJournalId, setViewJournalId] = useState(null);
    const [deleteEntry, setDeleteEntry] = useState(null);
    const [auditJournalId, setAuditJournalId] = useState(null);

    const societyId = selectedSocietyStorer.getSocietyId();

    const fetchJournals = useCallback(() => {
        if (!societyId) return;
        const params = {
            societyId,
            page: currentPage,
            limit,
        };
        if (filterEntryType && filterEntryType !== 'All Types' && filterEntryType !== 'all') {
            params.entryType = filterEntryType;
        }
        if (filterEntryFrom && filterEntryFrom !== 'All Sources' && filterEntryFrom !== 'all') {
            params.entryFlag = filterEntryFrom;
        }
        if (fromDate) {
            params.fromDate = fromDate;
        }
        if (toDate) {
            params.toDate = toDate;
        }
        dispatch(getJournals(params));
    }, [dispatch, societyId, currentPage, limit, filterEntryType, filterEntryFrom, fromDate, toDate]);
    usePageRefresh(fetchJournals);

    useEffect(() => {
        fetchJournals();
    }, [fetchJournals]);

    // Reset to page 1 when filters change
    useEffect(() => {
        setCurrentPage(1);
    }, [filterEntryType, filterEntryFrom, fromDate, toDate]);

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const journalEntries = journalsData || [];

    // Quote every field: remarks routinely contain commas, and a stray quote would
    // otherwise shift every following column.
    const csvCell = (value) => `"${String(value ?? '').replace(/"/g, '""')}"`;

    const handleDownloadCsv = () => {
        if (journalEntries.length === 0) return;
        const headers = ['JV No', 'Date', 'Entry Type', 'Source', 'Remarks', 'Reference No', 'Cheque No', 'Total Debit', 'Total Credit', 'Balanced'];
        const rows = journalEntries.map(entry => [
            entry.jv_no || entry.id,
            entry.date || '',
            ENTRY_TYPE_LABELS[entry.entry_type] || entry.entry_type || '',
            entry.entry_from || '',
            entry.remarks || '',
            entry.reff_no || '',
            entry.cheque_no || '',
            parseFloat(entry.totalDebit || 0).toFixed(2),
            parseFloat(entry.totalCredit || 0).toFixed(2),
            entry.isBalanced ? 'Yes' : 'No',
        ].map(csvCell).join(','));

        const csv = [headers.map(csvCell).join(','), ...rows].join('\n');
        // BOM so Excel opens UTF-8 correctly rather than mangling non-ASCII remarks.
        const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `journal_entries_${localDateString()}.csv`;
        a.click();
        URL.revokeObjectURL(url);
    };

    const stats = useMemo(() => {
        const totalValue = journalEntries.reduce((sum, entry) => {
            const debit = parseFloat(entry.totalDebit) || 0;
            const credit = parseFloat(entry.totalCredit) || 0;
            return sum + Math.max(debit, credit);
        }, 0);
        const drafts = journalEntries.filter(entry => entry.entry_flag === 'draft').length;
        const unbalanced = journalEntries.filter(entry => !entry.isBalanced).length;
        return { totalValue, drafts, unbalanced };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [journalEntries]);

    const formatCurrency = (amount) => parseFloat(amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

    const getTypeColor = (type) => {
        switch (type) {
            case 'payment': return 'tw-text-blue-600';
            case 'receipt': return 'tw-text-emerald-600';
            case 'daily': return 'tw-text-purple-600';
            case 'invoice': return 'tw-text-indigo-600';
            default: return 'tw-text-gray-600';
        }
    };

    const handleConfirmDelete = () => {
        if (!deleteEntry) return;
        dispatch(deleteJournal({
            societyId,
            journalId: deleteEntry.id,
            onSuccess: () => { setDeleteEntry(null); fetchJournals(); },
        }));
    };

    const totalPages = pagination?.totalPages || 1;
    const totalCount = pagination?.totalCount || journalEntries.length;

    const getPageNumbers = () => {
        const pages = [];
        for (let i = 1; i <= totalPages; i++) {
            pages.push(i);
        }
        return pages;
    };

    const startEntry = totalCount === 0 ? 0 : (currentPage - 1) * limit + 1;
    const endEntry = Math.min(currentPage * limit, totalCount);

    return (
        <div className="tw-p-4 tw-bg-gray-50 tw-min-h-screen">
            {/* Header */}
            <div className="tw-flex tw-items-center tw-justify-between">
                <div>
                    <h1 className="tw-text-base tw-font-semibold tw-text-gray-800">Manage Journals</h1>
                    <p className="tw-text-[11px] tw-text-gray-500 tw-mt-0.5">{totalCount} entries</p>
                </div>
                <div className="tw-flex tw-items-center tw-gap-2">
                    {/* Download CSV — exports the rows currently listed (the active
                        filters and page), not the whole ledger. */}
                    <button
                        onClick={handleDownloadCsv}
                        disabled={journalEntries.length === 0}
                        title={journalEntries.length === 0 ? 'Nothing to export' : 'Export the listed entries'}
                        className="tw-flex tw-items-center tw-gap-1.5 tw-bg-gray-50 tw-text-gray-700 tw-px-2.5 tw-py-1.5 tw-rounded-md tw-text-xs hover:tw-bg-gray-100 tw-border tw-border-gray-200 disabled:tw-opacity-50 disabled:tw-cursor-not-allowed"
                    >
                        <Icon icon="mdi:download-outline" className="tw-text-xs" />
                        Download CSV
                    </button>

                    {/* Bulk Import */}
                    <button
                        onClick={() => navigate(SOCIETY_PORTAL + SOCIETY_PORTAL_ACCOUNTS_JOURNAL_BULK_IMPORT)}
                        className="tw-flex tw-items-center tw-gap-1.5 tw-bg-gray-50 tw-text-gray-700 tw-px-2.5 tw-py-1.5 tw-rounded-md tw-text-xs hover:tw-bg-gray-100 tw-border tw-border-gray-200"
                    >
                        <Icon icon="mdi:upload-outline" className="tw-text-xs" />
                        Bulk Import
                    </button>

                    {/* New Journal Button */}
                    <button
                        onClick={() => navigate(SOCIETY_PORTAL + SOCIETY_PORTAL_ACCOUNTS_JOURNAL_ADD)}
                        className="tw-flex tw-items-center tw-gap-1 tw-bg-red-600 tw-text-white tw-px-3 tw-py-1.5 tw-rounded tw-text-xs tw-font-medium hover:tw-bg-red-700"
                    >
                        <Icon icon="mdi:plus" className="tw-text-xs" />
                        New Journal
                    </button>
                </div>
            </div>

            {/* Stats Cards */}
            <div className="tw-flex tw-items-center tw-gap-5 tw-border-b tw-border-gray-100">
                <div>
                    <p className="tw-text-[10px] tw-text-gray-500 tw-uppercase tw-tracking-wide">Total Value</p>
                    <p className="tw-text-lg tw-font-semibold tw-text-gray-800 tw-mt-0.5">₹{formatCurrency(stats.totalValue)}</p>
                </div>
                <div className="tw-w-px tw-h-8 tw-bg-gray-200"></div>
                <div>
                    <p className="tw-text-[10px] tw-text-gray-500 tw-uppercase tw-tracking-wide">Drafts</p>
                    <p className="tw-text-lg tw-font-semibold tw-text-amber-500 tw-mt-0.5">{stats.drafts}</p>
                </div>
                <div className="tw-w-px tw-h-8 tw-bg-gray-200"></div>
                <div>
                    <p className="tw-text-[10px] tw-text-gray-500 tw-uppercase tw-tracking-wide">Unbalanced</p>
                    <p className="tw-text-lg tw-font-semibold tw-text-red-500 tw-mt-0.5">{stats.unbalanced}</p>
                </div>
            </div>

            {/* Filter Fields */}
            <div className="tw-bg-white tw-rounded-lg tw-p-3 tw-mb-3 tw-shadow-sm tw-border tw-border-gray-100">
                <div className="tw-flex tw-items-center tw-gap-3 tw-flex-wrap">
                    <div>
                        <label className="tw-flex tw-items-center tw-gap-1 tw-text-[10px] tw-text-gray-500 tw-uppercase tw-tracking-wide tw-mb-1"><Icon icon="mdi:source-branch" className="tw-text-xs" /> Source</label>
                        <SearchableDropdown
                            value={filterEntryFrom}
                            onChange={setFilterEntryFrom}
                            options={ENTRY_FROM_OPTIONS}
                            placeholder="All Sources"
                        />
                    </div>

                    <div>
                        <label className="tw-flex tw-items-center tw-gap-1 tw-text-[10px] tw-text-gray-500 tw-uppercase tw-tracking-wide tw-mb-1"><Icon icon="mdi:tag-outline" className="tw-text-xs" /> Entry Type</label>
                        <SearchableDropdown
                            value={filterEntryType}
                            onChange={setFilterEntryType}
                            options={ENTRY_TYPES}
                            placeholder="All Types"
                        />
                    </div>

                    <div>
                        <label className="tw-flex tw-items-center tw-gap-1 tw-text-[10px] tw-text-gray-500 tw-uppercase tw-tracking-wide tw-mb-1"><Icon icon="mdi:calendar-start" className="tw-text-xs" /> From Date</label>
                        <div className="tw-relative">
                            <input
                                type="date"
                                value={fromDate}
                                onChange={(e) => setFromDate(e.target.value)}
                                className="tw-px-3 tw-pr-8 tw-py-2 tw-border tw-border-gray-200 tw-rounded-md tw-text-sm focus:tw-outline-none focus:tw-border-blue-500 tw-w-[160px]"
                            />
                            {fromDate && (
                                <Icon
                                    icon="mdi:close-circle"
                                    className="tw-absolute tw-right-2 tw-top-1/2 tw--translate-y-1/2 tw-text-gray-400 hover:tw-text-red-500 tw-text-sm tw-cursor-pointer"
                                    onClick={() => setFromDate('')}
                                />
                            )}
                        </div>
                    </div>

                    <div>
                        <label className="tw-flex tw-items-center tw-gap-1 tw-text-[10px] tw-text-gray-500 tw-uppercase tw-tracking-wide tw-mb-1"><Icon icon="mdi:calendar-end" className="tw-text-xs" /> To Date</label>
                        <div className="tw-relative">
                            <input
                                type="date"
                                value={toDate}
                                onChange={(e) => setToDate(e.target.value)}
                                className="tw-px-3 tw-pr-8 tw-py-2 tw-border tw-border-gray-200 tw-rounded-md tw-text-sm focus:tw-outline-none focus:tw-border-blue-500 tw-w-[160px]"
                            />
                            {toDate && (
                                <Icon
                                    icon="mdi:close-circle"
                                    className="tw-absolute tw-right-2 tw-top-1/2 tw--translate-y-1/2 tw-text-gray-400 hover:tw-text-red-500 tw-text-sm tw-cursor-pointer"
                                    onClick={() => setToDate('')}
                                />
                            )}
                        </div>
                    </div>

                </div>
            </div>

            {/* Table */}
            <div className="tw-bg-white tw-rounded-lg tw-shadow-sm tw-border tw-border-gray-100 tw-overflow-hidden">
                <table className="tw-w-full tw-text-xs">
                    <thead>
                        <tr className="tw-bg-gray-50 tw-border-b tw-border-gray-100">
                            <th className="tw-text-left tw-px-3 tw-py-2 tw-text-[10px] tw-font-medium tw-text-gray-500 tw-uppercase tw-tracking-wide tw-w-[36px]">
                                <Icon icon="mdi:arrow-up-down" className="tw-text-gray-400 tw-text-xs" />
                            </th>
                            <th className="tw-text-left tw-px-3 tw-py-2 tw-text-[10px] tw-font-medium tw-text-gray-500 tw-uppercase tw-tracking-wide">JV.NO #</th>
                            <th className="tw-text-left tw-px-3 tw-py-2 tw-text-[10px] tw-font-medium tw-text-gray-500 tw-uppercase tw-tracking-wide">Remarks</th>
                            <th className="tw-text-left tw-px-3 tw-py-2 tw-text-[10px] tw-font-medium tw-text-gray-500 tw-uppercase tw-tracking-wide">Date</th>
                            <th className="tw-text-right tw-px-3 tw-py-2 tw-text-[10px] tw-font-medium tw-text-gray-500 tw-uppercase tw-tracking-wide">Total Debit (₹)</th>
                            <th className="tw-text-right tw-px-3 tw-py-2 tw-text-[10px] tw-font-medium tw-text-gray-500 tw-uppercase tw-tracking-wide">Total Credit (₹)</th>
                            <th className="tw-text-left tw-px-3 tw-py-2 tw-text-[10px] tw-font-medium tw-text-gray-500 tw-uppercase tw-tracking-wide">Type</th>
                            <th className="tw-text-left tw-px-3 tw-py-2 tw-text-[10px] tw-font-medium tw-text-gray-500 tw-uppercase tw-tracking-wide">Action</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading ? (
                            <tr>
                                <td colSpan="8" className="tw-text-center tw-py-12">
                                    <div className="tw-flex tw-items-center tw-justify-center tw-gap-2">
                                        <div className="tw-animate-spin tw-rounded-full tw-h-5 tw-w-5 tw-border-2 tw-border-gray-300 tw-border-t-blue-500"></div>
                                        <span className="tw-text-gray-500 tw-text-sm">Loading journal entries...</span>
                                    </div>
                                </td>
                            </tr>
                        ) : journalEntries.map((entry) => {
                            const lineCount = (entry.debits?.length || 0) + (entry.credits?.length || 0);
                            const isExpanded = expandedId === entry.id;
                            return (
                                <React.Fragment key={entry.id}>
                                    <tr className="tw-border-b tw-border-gray-50 hover:tw-bg-gray-50 tw-cursor-pointer" onClick={() => setExpandedId(isExpanded ? null : entry.id)}>
                                        <td className="tw-px-3 tw-py-2">
                                            <Icon icon={isExpanded ? 'mdi:chevron-down' : 'mdi:chevron-right'} className="tw-text-gray-400 tw-text-xs hover:tw-text-gray-600" />
                                        </td>
                                        <td className="tw-px-3 tw-py-2">
                                            <div>
                                                <span className="tw-font-semibold tw-text-gray-800">{entry.id}</span>
                                                <span className="tw-text-[10px] tw-text-gray-500 tw-ml-1.5">{lineCount} lines</span>
                                            </div>
                                        </td>
                                        <td className="tw-px-3 tw-py-2 tw-text-gray-600 tw-max-w-[220px] tw-truncate">{entry.remarks || '-'}</td>
                                        <td className="tw-px-3 tw-py-2 tw-text-gray-600">{entry.date}</td>
                                        <td className="tw-px-3 tw-py-2 tw-text-right">
                                            <span className="tw-text-emerald-600 tw-font-medium">{formatCurrency(entry.totalDebit)}</span>
                                        </td>
                                        <td className="tw-px-3 tw-py-2 tw-text-right">
                                            <span className="tw-text-red-600 tw-font-medium">{formatCurrency(entry.totalCredit)}</span>
                                        </td>
                                        <td className="tw-px-3 tw-py-2">
                                            <div className="tw-flex tw-items-center tw-gap-1">
                                                <Icon icon="mdi:credit-card-outline" className="tw-text-blue-500 tw-text-xs" />
                                                <span className={getTypeColor(entry.entry_type)}>
                                                    {ENTRY_TYPE_LABELS[entry.entry_type] || entry.entry_type}
                                                </span>
                                            </div>
                                        </td>
                                        <td className="tw-px-3 tw-py-2" onClick={(e) => e.stopPropagation()}>
                                            <div className="tw-flex tw-items-center tw-gap-0.5">
                                                <button onClick={() => setViewJournalId(entry.id)} className="tw-p-1 tw-rounded hover:tw-bg-gray-100 tw-text-gray-400 hover:tw-text-gray-600" title="View">
                                                    <Icon icon="mdi:eye-outline" className="tw-text-xs" />
                                                </button>
                                                <button onClick={() => navigate(SOCIETY_PORTAL + SOCIETY_PORTAL_ACCOUNTS_JOURNAL_EDIT.replace(':journalId', entry.id))} className="tw-p-1 tw-rounded hover:tw-bg-gray-100 tw-text-gray-400 hover:tw-text-gray-600" title="Edit">
                                                    <Icon icon="mdi:pencil-outline" className="tw-text-xs" />
                                                </button>
                                                <button
                                                    className="tw-p-1 tw-rounded hover:tw-bg-gray-100 tw-text-gray-400 hover:tw-text-red-600"
                                                    title="Delete"
                                                    onClick={() => setDeleteEntry(entry)}
                                                    disabled={deleteLoading}
                                                >
                                                    <Icon icon="mdi:trash-can-outline" className="tw-text-xs" />
                                                </button>
                                                <button onClick={() => setAuditJournalId(entry.id)} className="tw-p-1 tw-rounded hover:tw-bg-gray-100 tw-text-gray-400 hover:tw-text-gray-600" title="Audit Logs">
                                                    <Icon icon="mdi:history" className="tw-text-xs" />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                    {isExpanded && (
                                        <tr className="tw-bg-gray-50">
                                            <td colSpan="8" className="tw-px-3 tw-py-2">
                                                <div className="tw-flex tw-gap-5">
                                                    {/* Debits */}
                                                    <div className="tw-flex-1">
                                                        <p className="tw-text-[10px] tw-font-semibold tw-text-emerald-700 tw-uppercase tw-tracking-wide tw-mb-2 tw-flex tw-items-center tw-gap-1">
                                                            <Icon icon="mdi:arrow-top-right" className="tw-text-xs" /> Debits
                                                        </p>
                                                        <div className="tw-space-y-1.5">
                                                            {entry.debits?.map((d) => (
                                                                <div key={d.id} className="tw-flex tw-items-center tw-justify-between tw-bg-white tw-rounded tw-px-3 tw-py-2 tw-border tw-border-gray-100">
                                                                    <div>
                                                                        <p className="tw-text-xs tw-font-medium tw-text-gray-800">{d.account_name}</p>
                                                                        <p className="tw-text-[10px] tw-text-gray-400">{d.account_cd}</p>
                                                                    </div>
                                                                    <span className="tw-text-xs tw-font-semibold tw-text-emerald-600">₹{formatCurrency(d.amount)}</span>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    </div>
                                                    {/* Credits */}
                                                    <div className="tw-flex-1">
                                                        <p className="tw-text-[10px] tw-font-semibold tw-text-red-700 tw-uppercase tw-tracking-wide tw-mb-2 tw-flex tw-items-center tw-gap-1">
                                                            <Icon icon="mdi:arrow-bottom-left" className="tw-text-xs" /> Credits
                                                        </p>
                                                        <div className="tw-space-y-1.5">
                                                            {entry.credits?.map((c) => (
                                                                <div key={c.id} className="tw-flex tw-items-center tw-justify-between tw-bg-white tw-rounded tw-px-3 tw-py-2 tw-border tw-border-gray-100">
                                                                    <div>
                                                                        <p className="tw-text-xs tw-font-medium tw-text-gray-800">{c.account_name}</p>
                                                                        <p className="tw-text-[10px] tw-text-gray-400">{c.account_cd}</p>
                                                                    </div>
                                                                    <span className="tw-text-xs tw-font-semibold tw-text-red-600">₹{formatCurrency(c.amount)}</span>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    </div>
                                                </div>
                                                {/* Extra info */}
                                                <div className="tw-flex tw-items-center tw-gap-4 tw-mt-3 tw-pt-2 tw-border-t tw-border-gray-200 tw-text-[10px] tw-text-gray-500">
                                                    {entry.reff_no && <span><strong>Ref:</strong> {entry.reff_no}</span>}
                                                    {entry.invoice_no && <span><strong>Invoice:</strong> {entry.invoice_no}</span>}
                                                    <span><strong>Flag:</strong> {entry.entry_flag}</span>
                                                    <span className={`tw-font-medium ${entry.isBalanced ? 'tw-text-emerald-600' : 'tw-text-red-600'}`}>
                                                        {entry.isBalanced ? 'Balanced' : 'Unbalanced'}
                                                    </span>
                                                </div>
                                            </td>
                                        </tr>
                                    )}
                                </React.Fragment>
                            );
                        })}
                    </tbody>
                </table>
                {!loading && journalEntries.length === 0 && (
                    <div className="tw-text-center tw-py-10">
                        <Icon icon="mdi:book-open-page-variant" className="tw-text-3xl tw-text-gray-300 tw-mx-auto" />
                        <p className="tw-text-gray-500 tw-mt-2 tw-text-xs">No journal entries found</p>
                    </div>
                )}
            </div>

            {/* Pagination */}
            <div className="tw-flex tw-items-center tw-justify-between tw-mt-3 tw-text-xs">
                <p className="tw-text-gray-500">
                    Showing <span className="tw-font-medium tw-text-gray-700">{startEntry}-{endEntry}</span> of{' '}
                    <span className="tw-font-medium tw-text-gray-700">{totalCount}</span>
                </p>
                <div className="tw-flex tw-items-center tw-gap-0.5">
                    <button
                        className="tw-p-1.5 tw-rounded hover:tw-bg-gray-100 tw-text-gray-400 disabled:tw-opacity-50"
                        disabled={!pagination?.hasPrevPage}
                        onClick={() => setCurrentPage(prev => prev - 1)}
                    >
                        <Icon icon="mdi:chevron-left" className="tw-text-xs" />
                    </button>
                    {getPageNumbers().map(page => (
                        <button
                            key={page}
                            onClick={() => setCurrentPage(page)}
                            className={`tw-px-2.5 tw-py-1.5 tw-rounded tw-text-xs tw-font-medium ${
                                page === currentPage
                                    ? 'tw-bg-gray-800 tw-text-white'
                                    : 'hover:tw-bg-gray-100 tw-text-gray-600'
                            }`}
                        >
                            {page}
                        </button>
                    ))}
                    <button
                        className="tw-p-1.5 tw-rounded hover:tw-bg-gray-100 tw-text-gray-400 disabled:tw-opacity-50"
                        disabled={!pagination?.hasNextPage}
                        onClick={() => setCurrentPage(prev => prev + 1)}
                    >
                        <Icon icon="mdi:chevron-right" className="tw-text-xs" />
                    </button>
                </div>
            </div>
            {/* View Modal */}
            <ViewJournalModal journalId={viewJournalId} onClose={() => setViewJournalId(null)} />

            {/* Delete Confirmation Modal */}
            <DeleteJournalModal entry={deleteEntry} onClose={() => setDeleteEntry(null)} onConfirm={handleConfirmDelete} loading={deleteLoading} />

            {/* Audit Logs Modal */}
            <AuditLogsModal journalId={auditJournalId} onClose={() => setAuditJournalId(null)} />
        </div>
    );
};

export default Journal;
